// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ShareEntityButton, { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickableProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

const enum fields {
    generateEmailAttachmentFromWebpage = "generateEmailAttachmentFromWebpage",
    // XNG-Changes: field to determine whether we're showing title/actions/both
    show = "show",
}

export interface InvoiceDetailHeaderStyles {
    shareEntityButtonStyles?: ShareEntityButtonStyles;
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    headerGridContainer?: GridContainerProps;
    narrowOverflowMenu?: OverflowMenuProps;
    title: TypographyProps;
    titleGridItem: GridItemProps;
    printButton?: ButtonPresentationProps;
    buttonGridItem?: GridItemProps;
    printClickable?: ClickableProps;
    emailClickable?: ClickableProps;
}

export const headerStyles: InvoiceDetailHeaderStyles = {
    shareEntityButtonStyles: {
        button: {
            buttonType: "outline",
            variant: "secondary",
        },
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            margin-left: 10px;
        `,
    },
    buttonGridItem: {
        css: css`
            justify-content: flex-end;
        `,
        width: [1, 1, 1, 6, 6],
    },
    titleGridItem: {
        width: [11, 11, 11, 6, 6],
    },
    title: {
        variant: "h2",
        as: "h1",
    },
    buttonsHiddenContainer: {
        below: "lg",
    },
    menuHiddenContainer: {
        above: "md",
    },
};

const styles = headerStyles;
const InvoiceDetailHeader: FC<WidgetProps> = ({ fields }) => {
    const { value: invoice } = useContext(InvoiceStateContext);
    const resolvedGenerateEmailAttachmentFromWebpage =
        fields.generateEmailAttachmentFromWebpage === true || fields.generateEmailAttachmentFromWebpage === undefined;
    // XNG-Changes
    const { show } = fields;
    if (!invoice) {
        return null;
    }

    const printLabel = translate("Print");

    // XNG-Changes: remove Overflow menu and check for show field
    return (
        <GridContainer {...styles.headerGridContainer}>
            {(show === "title" || show === "both") && (
                <GridItem {...styles.titleGridItem}>
                    <Typography {...styles.title}>{`${translate("Invoice #")} ${invoice.invoiceNumber}`}</Typography>
                </GridItem>
            )}
            {(show === "actions" || show === "both") && (
                <GridItem {...styles.buttonGridItem} data-test-selector="InvoiceDetails-Actions">
                    {/* <Hidden {...styles.menuHiddenContainer}>
                        <OverflowMenu hasChildPortal position="end" {...styles.narrowOverflowMenu}>
                            <ShareEntityButton
                                entityId={invoice.invoiceNumber}
                                variant="clickable"
                                entityName="Invoice"
                                extendedStyles={styles.shareEntityButtonStyles}
                                generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
                            />
                            <Clickable
                                {...styles.printClickable}
                                onClick={openPrintDialog}
                                data-test-selector="invoiceDetails_print"
                            >
                                {printLabel}
                            </Clickable>
                        </OverflowMenu>
                    </Hidden> */}
                    <ShareEntityButton
                        entityId={invoice.invoiceNumber}
                        entityName="Invoice"
                        extendedStyles={styles.shareEntityButtonStyles}
                        generateAttachmentFromWebpage={resolvedGenerateEmailAttachmentFromWebpage}
                    />
                    <Button {...styles.printButton} onClick={openPrintDialog} data-test-selector="invoiceDetails_print">
                        {printLabel}
                    </Button>
                </GridItem>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: InvoiceDetailHeader,
    definition: {
        displayName: "Page Header",
        allowedContexts: ["InvoiceDetailsPage"],
        group: "Invoice Details",
        fieldDefinitions: [
            {
                name: fields.generateEmailAttachmentFromWebpage,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tooltip:
                    "If checked, sharing the invoice generates an email attachment using the Invoice Details page, rather than a pre-defined PDF file.",
            },
            // XNG-Changes
            {
                name: "show",
                displayName: "Show",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Title", value: "title" },
                    { displayName: "Actions", value: "actions" },
                    { displayName: "Both", value: "both" },
                ],
                fieldType: "General",
                defaultValue: "title",
            },
        ],
    },
};

export default widgetModule;
